import * as Y from "yjs";
import {
  Col,
  css,
  CSSProps,
  cx,
  DSButton,
  DSIconButton,
  DSIconFocusMode,
  DSIconPalette,
  DSIconPencil,
  DSIconTrash,
  Row,
  Text,
} from "@cdx/ds";
import {BushRect, clampZoom, getAnchorPos, VisionBoardBag} from "./canvas-utils";
import {EdgeBushEntry, NodeBushEntry} from "./useVisionBoardData";
import {ArrowOverlay, DropDownForChild, TooltipForChild, useGlobalKeyPress} from "@cdx/common";
import {VisionBoardColor, VisionBoardNode} from "../../cdx-models/utils/extended-fields";
import {duplicateBushEntryNode} from "./canvas-node-utils";
import {canvasNodeStyles} from "./vision-board.css";
import {msNextThemes} from "../../pages/milestones/Timeline/Timeline.css";
import {useEdgeColor} from "./CanvasEdge";
import {CSSProperties, ReactNode, useEffect, useState} from "react";

const Swatch = ({color}: {color: VisionBoardColor}) => (
  <div className={cx(canvasNodeStyles.colorSwatch, msNextThemes[color])} />
);

const cols = ["green", "blue", "pink", "red", "yellow", "brown"] as const;

const ColorPickerOverlay = ({
  onColor,
  currColor,
  overlayProps,
}: {
  overlayProps: any;
  onColor: (color: VisionBoardColor | null) => void;
  currColor: VisionBoardColor | null;
}) => {
  return (
    <ArrowOverlay
      bg="gray800"
      outerClassName={css({colorTheme: "gray850"})}
      arrowSize="sm"
      {...overlayProps}
    >
      <Col pa="16px" sp="12px">
        <Row align="center">
          <Text type="label12" color="primary">
            Pick Color
          </Text>
          <DSButton
            size="sm"
            variant="secondary"
            onClick={() => onColor(null)}
            className={css({ml: "auto"})}
            active={!currColor}
          >
            None
          </DSButton>
        </Row>
        <Row sp="12px" align="center">
          {cols.map((color) => (
            <DSIconButton
              key={color}
              size="md"
              variant="tertiary"
              negatePadding
              onClick={() => onColor(color)}
              icon={<Swatch color={color} />}
              active={color === currColor}
            />
          ))}
        </Row>
      </Col>
    </ArrowOverlay>
  );
};

const ColorPicker = ({
  onColor,
  currColor,
}: {
  onColor: (color: VisionBoardColor | null) => void;
  currColor: VisionBoardColor | null;
}) => (
  <DropDownForChild
    setChildProps={({isOpen}: any) => ({
      tooltip: isOpen ? null : "Set Color",
      active: isOpen,
    })}
    renderOverlay={(overlayProps: any) => (
      <ColorPickerOverlay overlayProps={overlayProps} onColor={onColor} currColor={currColor} />
    )}
    overlayProps={{placement: "top", distanceFromAnchor: 10}}
  >
    <TooltipForChild tooltip={null}>
      <DSIconButton variant="secondary" icon={<DSIconPalette />} />
    </TooltipForChild>
  </DropDownForChild>
);

export const FocusNodeMenuItem = ({
  getRect,
  bag,
}: {
  getRect: () => BushRect;
  bag: VisionBoardBag;
}) => {
  const {posRef, positionCanvas} = bag;
  const handleFocus = () => {
    const rect = getRect();
    const w = rect.maxX - rect.minX;
    const h = rect.maxY - rect.minY;

    const midX = rect.minX + w / 2;
    const midY = rect.minY + h / 2;
    const [canvasW, canvasH] = bag.canvasMeasureRef.current;
    const xScale = (0.8 * canvasW) / w;
    const yScale = (0.8 * canvasH) / h;
    const nextScale = clampZoom(Math.min(xScale, yScale));
    posRef.current.scale = nextScale;
    posRef.current.x = -(midX - canvasW / nextScale / 2);
    posRef.current.y = -(midY - canvasH / nextScale / 2);
    positionCanvas();
  };
  return (
    <TooltipForChild tooltip="Focus Node">
      <DSIconButton variant="secondary" icon={<DSIconFocusMode />} onClick={handleFocus} />
    </TooltipForChild>
  );
};

const DeleteNodeMenuItem = ({onDelete, tooltip}: {onDelete: () => void; tooltip: string}) => {
  useGlobalKeyPress({fn: onDelete, key: "Backspace"});
  useGlobalKeyPress({fn: onDelete, key: "Delete"});
  return (
    <TooltipForChild tooltip={tooltip}>
      <DSIconButton variant="secondary" icon={<DSIconTrash />} onClick={onDelete} />
    </TooltipForChild>
  );
};

type DefaultNodeMenuItemsProps = {
  node: NodeBushEntry;
  bag: VisionBoardBag;
  nodeData: VisionBoardNode;
  onDelete: undefined | ((nodeData: VisionBoardNode) => void);
};
export const DefaultNodeMenuItems = ({
  bag,
  node,
  nodeData,
  onDelete,
}: DefaultNodeMenuItemsProps) => {
  const handleDuplicate = () => {
    const {id: newId} = duplicateBushEntryNode(bag, node);
    bag.selectionStore.setNodeIds([newId]);
  };
  useGlobalKeyPress({fn: handleDuplicate, code: "KeyD", withMod: true});

  const {id, node: nodeMap} = node;
  const handleDelete = () => {
    if (onDelete) onDelete(nodeData);
    (nodeMap.parent as Y.Map<any>).delete(id);
  };

  const setColor = (color: null | VisionBoardColor) => {
    node.node.set("color", color ?? undefined);
  };

  return (
    <>
      <ColorPicker currColor={nodeData.color ?? null} onColor={setColor} />
      <FocusNodeMenuItem bag={bag} getRect={() => node} />
      <DeleteNodeMenuItem tooltip="Delete Node" onDelete={handleDelete} />
    </>
  );
};

export const CanvasNodeMenu = ({
  children,
  style,
  className,
  menuOffset,
}: {
  children: ReactNode;
  style?: CSSProperties;
  className?: string;
  menuOffset?: CSSProps["pb"];
}) => (
  <Col
    absolute
    bottom="100%"
    left="0"
    right="0"
    pb={menuOffset || "8px"}
    align="center"
    pointerEvents="none"
    zIndex={1}
    style={style}
    className={className}
  >
    <div
      className={css({
        display: "flex",
        bg: "foreground",
        colorTheme: "white",
        pa: "8px",
        rounded: 4,
        pointerEvents: "auto",
        sp: "8px",
      })}
      style={{transform: "scale(calc(1 / var(--canvas-scale)))", transformOrigin: "50% 100%"}}
      onMouseDown={stopPropagation}
    >
      {children}
    </div>
  </Col>
);

const stopPropagation = (e: any) => {
  e.stopPropagation();
};

type CanvasEdgeMenuProps = {
  edge: EdgeBushEntry;
  bag: VisionBoardBag;
};
export const CanvasEdgeMenu = ({edge, bag}: CanvasEdgeMenuProps) => {
  const {id, edge: edgeMap} = edge;
  const handleDelete = () => {
    (edgeMap.parent as Y.Map<any>).delete(id);
  };

  const getRect = () => {
    const [sx, sy] = getAnchorPos(edge.source);
    const [ex, ey] = getAnchorPos(edge.target);
    const [minX, maxX] = sx < ex ? [sx, ex] : [ex, sx];
    const [minY, maxY] = sy < ey ? [sy, ey] : [ey, sy];
    return {minX, minY, maxX, maxY};
  };

  const currColor = useEdgeColor(edge);

  const setColor = (color: null | VisionBoardColor) => {
    edge.edge.set("color", color ?? undefined);
  };

  return (
    <CanvasNodeMenu style={{margin: -100}}>
      <TooltipForChild tooltip="Edit Label">
        <DSIconButton
          variant="secondary"
          icon={<DSIconPencil />}
          onClick={() => bag.selectionStore.setEdgeId(edge.id, "edit")}
        />
      </TooltipForChild>
      <ColorPicker currColor={currColor ?? null} onColor={setColor} />
      <FocusNodeMenuItem getRect={getRect} bag={bag} />
      <DeleteNodeMenuItem tooltip="Delete Connection" onDelete={handleDelete} />
    </CanvasNodeMenu>
  );
};

const useCombinedColor = (bag: VisionBoardBag, nodes: NodeBushEntry[]) => {
  const [, update] = useState({});
  useEffect(() => {
    return bag.store.registerAnyUpdateListener(() => update({}));
  }, [bag.store]);
  const nodeCols = nodes.map((n) => n.node.get("color") as VisionBoardColor | null);
  return nodeCols.reduce((m, next) => (m === next ? m : null));
};

export const MultiNodeMenu = ({
  nodes,
  bag,
  rect,
}: {
  nodes: NodeBushEntry[];
  bag: VisionBoardBag;
  rect: BushRect;
}) => {
  const handleDelete = () => {
    bag.store.doc.transact(() => {
      for (const node of nodes) {
        const {id, node: nodeMap} = node;
        (nodeMap.parent as Y.Map<any>).delete(id);
      }
    });
  };

  const currColor = useCombinedColor(bag, nodes);

  const setColor = (color: null | VisionBoardColor) => {
    bag.store.doc.transact(() => {
      for (const node of nodes) {
        node.node.set("color", color ?? undefined);
      }
    });
  };

  return (
    <CanvasNodeMenu>
      <ColorPicker currColor={currColor ?? null} onColor={setColor} />
      <FocusNodeMenuItem getRect={() => rect} bag={bag} />
      <DeleteNodeMenuItem tooltip="Delete Nodes" onDelete={handleDelete} />
    </CanvasNodeMenu>
  );
};
